import { motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import cx from "classnames";

export type TypewriterProps = {
  children: string;
  delay?: number; // seconds
  speed?: number; // ms per character
  cursor?: boolean;
  pauseOnPunctuation?: boolean;
  cursorFadeOut?: boolean;
  className?: string;
  onUpdate?: (isComplete?: boolean) => void;
};

/**
 * Typewriter component with blinking cursor
 */
export default function Typewriter({
  children,
  delay = 0,
  speed = 50,
  cursor = true,
  pauseOnPunctuation = true,
  cursorFadeOut = true,
  className,
  onUpdate,
}: TypewriterProps) {
  // Use the children as the text to type out
  const text = children;

  // Split text into grapheme clusters so emojis render correctly
  const graphemes = useMemo(() => {
    // Check if Intl.Segmenter is available (not supported in some browsers like 2024 Firefox lmao)
    if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
      // Initialise a new segmenter
      const segmenter = new Intl.Segmenter(undefined, {
        granularity: "grapheme",
      });

      // Segment the text into graphemes
      return Array.from(segmenter.segment(text), (s) => s.segment);
    }

    // Fallback: Array.from handles most emojis reasonably well (but it'll break multi-grapheme emojis)
    return Array.from(text);
  }, [text]);

  const [displayedText, setDisplayedText] = useState(""); // Start with a blank character to avoid layout shift
  const [index, setIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(cursor);
  const [shouldDelay, setShouldDelay] = useState(delay > 0);

  useEffect(() => {
    // Stop if we've typed the whole text
    if (index >= graphemes.length) {
      // Hide the cursor after a delay if cursorFadeOut is enabled
      if (cursor && cursorFadeOut) {
        setTimeout(() => {
          setShowCursor(false);
        }, delay * 1600 + 2900);
      }

      if (!cursor) {
        setShowCursor(false);
      }

      return;
    }

    // Handle initial delay
    if (shouldDelay) {
      const delayTimeout = setTimeout(() => {
        setShouldDelay(false);
      }, delay * 1000);
      return () => clearTimeout(delayTimeout);
    }

    // Timeout between characters
    const timeout = setTimeout(() => {
      // Clear the blank character if this is the first character
      if (displayedText === "⠀") {
        setDisplayedText("");
      }

      // Find the next character to add
      const char = graphemes[index];

      if (!char) return; // No more characters to add

      // If pauseOnPunctuation is enabled and the character is punctuation, add a longer pause
      if (pauseOnPunctuation && /[.,!?;:]/.test(char)) {
        setTimeout(() => {
          setDisplayedText((prev) => prev + char);
          setIndex((i) => i + 1);
        }, speed * 8);
        return;
      }

      // Handle emoji addition (special handling for making emoji addition feel less jarring)
      if (/\p{Extended_Pictographic}/u.test(char)) {
        // Add three empty spaces first to make the emoji insertion less jarring
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            setDisplayedText((prev) => prev + "⠀"); // Unicode U+2800 Braille Pattern Blank (we use this instead of spaces to avoid collapsing)
          }, speed * (i + 1));
        }

        // After a short delay, replace the spaces with the actual emoji
        setTimeout(() => {
          setDisplayedText((prev) => prev.slice(0, -3) + char);
        }, speed * 3);

        setIndex((i) => i + 1);
        return;
      }

      // No special handling required, add the character to the displayed text
      setDisplayedText((prev) => prev + graphemes[index]);
      setIndex((i) => i + 1);

      return;
    }, speed);

    // Clear timeout when finished
    return () => clearTimeout(timeout);
  }, [index, graphemes, shouldDelay]);

  useEffect(() => {
    if (onUpdate) {
      onUpdate(displayedText.length == children.length);
    }
  }, [displayedText, onUpdate]);

  return (
    <div className={className}>
      <span>{displayedText}</span>
      {showCursor && (
        <motion.span
          aria-hidden
          className={cx("")}
          animate={{ opacity: [0, 1, 0] }}
          transition={{
            repeat: Infinity,
            duration: 0.8,
          }}
        >
          |
        </motion.span>
      )}
    </div>
  );
}
